/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useRef, useEffect } from "react";
import { useItemDrag } from "cms/common/hooks/use-item-drag";
import { useItemDrop } from "cms/common/hooks/use-item-drop";
import { ReactComponent as GripSVG } from "../../assets/images/icons/grip-dots-vertical-solid.svg";

import styles from "./sortable-content.module.css";
import cn from "classnames";

/**
 * @param {object} props
 * @param {string} props.id
 * @param {number} props.index
 * @param {import('react').ReactNode} props.children
 * @param {boolean} props.disabled
 * @param {SortableMethods} props.sortableMethods
 * @param {string=} props.className
 */
export const SortableContent = (props) => {

    const id = props?.id;
    const index = props?.index;
    const children = props?.children;
    const disabled = props?.disabled;
    const sortableMethods = props?.sortableMethods;
    const setDragActive = props?.setDragActive;
    
    const swapItems = sortableMethods?.swapItems;
    const findItem = sortableMethods?.findItem;
    const onDrop = sortableMethods?.onDrop;

    const dragRef = useRef(null);
    const dropRef = useRef(null);

    const [, drop] = useItemDrop(id, dropRef, findItem, swapItems);
    const [{ isDragging }, drag, preview] = useItemDrag(id, index, onDrop, swapItems);

    drag(dragRef);
    drop(preview(dropRef));
    
    useEffect(() => {
        if (typeof setDragActive === "function") {
            setDragActive(isDragging);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);
    
    if (isDragging) {
        return (
            <div
                ref={dropRef}
                className={cn(styles.draggable, props.className)}
                style={{ opacity: isDragging ? 0.4 : 1 }}
            >
                <div className="drop-location"/>
            </div>
        );
    }
    
    return (
        <div
            ref={dropRef}
            className={cn(styles.draggable, props.className)}
            style={{ opacity: isDragging ? 0.4 : 1 }}
        >
            <div
                ref={dragRef}
                className={styles.handle}
                style={{ display: disabled ? "none" : "flex", padding: "12px 4px 12px 12px" }}
            >
                <GripSVG className={styles.grip} />
            </div>
            
            {children}
            
        </div>
    );
};
